import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { GlobalState } from './global';
import { SSETalkAccessChannel } from './modules/talk/sse';
import { DataCache } from './shared/data/cache';
import { IdleTimer } from './shared/helpers/idle.helpers';
import { hasRouteAccess } from './shared/helpers/permissions.helpers';
import { Auth } from './shared/services/auth.service';
import { BrowserPush } from './shared/services/browser-push';
import { SessionManagement } from './shared/services/session-management';

export function useAppAuth() {
    const router = useRouter();

    const hasLoadedUserSession = ref(false);
    const hasLoadedPermissions = computed(() => GlobalState.permissions.value !== null);
    const hasUserSwitched = ref(false);
    const isAuthorized = computed(() => hasRouteAccess(router.currentRoute.value.path));

    watch(GlobalState.user, (user, previousUser) => {
        document.body.classList[user ? 'add' : 'remove']('signed-in');
        hasUserSwitched.value = hasUserSwitched.value || !!previousUser;
        if (user) {
            IdleTimer.notifyServerNotIdle();
            BrowserPush.refreshNotificationRegistration();
        }
    });

    watch(hasLoadedPermissions, () => {
        if (!GlobalState.user.value) return;
        if (!hasLoadedPermissions.value) return;
        if (hasUserSwitched.value && !isAuthorized.value) {
            router.replace('/');
        }
        hasUserSwitched.value = false;
    });

    onMounted(() => {
        void Auth.consumeOneTimeLoginFromUrl()
            .then(consumed => (consumed ? undefined : Auth.loadExistingSessionInitial()))
            .finally(() => {
                hasLoadedUserSession.value = true;
            });
    });

    SSETalkAccessChannel.use(() => {
        DataCache.reloadCache('talkAccess', true);
        // A workstation-extension-link change publishes this same event to the workstation's `w:`
        // channel (see docs/talk-workstation-extension-plan.md §1.4) — reload the link cache too.
        if (GlobalState.isWorkstation) DataCache.reloadCache('talkWorkstationExtension', true);
    });

    SessionManagement.install();

    return { hasLoadedPermissions, hasLoadedUserSession, isAuthorized };
}
